import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { SessionListProps, TAB_TO_BACKEND_FILTER } from './cfg';
import { roundDateDown } from '../../filters/slice';
import { SessionHistoryTabs, SessionDetailView } from '../../../../components/History/views';
import { SessionState, fromReduxSafeSessionListResponses } from './list-slice';
import { SessionListResponse } from '../../../../interfaces/sessions';
import { BackendFilter, FilterOperator } from '../../../../interfaces/sessions';
import { BackendTables } from '../../../../interfaces/db';
import { SessionAgentsColumns } from '../../../../interfaces/table';
import { UserDataResult } from '../../../../interfaces/users';

// Local type for Redux-safe user data (matches the one in users/slice but not exported)
type ReduxSafeUserDataResult = Omit<UserDataResult, 'created_at'> & { created_at: number; };

const MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000;
const ABSOLUTE_MAX_LOOKBACK_MONTHS = 12;
const MAX_DATE_LOOKBACK_MONTHS = 6;
const MIN_DESIRED_ITEMS = 60;
// Add cleanup constants
const ENHANCEMENT_CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Extended session state with our additional tracking properties
interface ExtendedSessionState {
  items: SessionListResponse[]; // Convert to Date objects only when needed
  startRequested: string | null; // Keep as ISO string
  hasMore: boolean;
  startHasMore: boolean;
  isLoading: boolean;
  isDebouncing: boolean;
  error: string | null;
  lastRequestKey: string | null;
  totalItems: number;
  backoffRetryCount: number;
  noResultsAfterBackoff: boolean;
  monthsSearched: number;
  hitAbsoluteMaxLookback?: boolean;
}

/**
 * Generate the combined filter based on passed filter, tab, view, and user
 */
function generateCombinedFilter(
  passed_filter: BackendFilter | null | undefined,
  tab: SessionHistoryTabs,
  view: SessionDetailView,
  user: ReduxSafeUserDataResult | null
): BackendFilter {
  const tab_filter = TAB_TO_BACKEND_FILTER[tab] || [];
  
  // Ensure passed_filter is always an array
  const adjusted_filter = passed_filter ? [...passed_filter] : [];
  if (user && view === SessionDetailView.COACHING && ![SessionHistoryTabs.ALL_HISTORY, SessionHistoryTabs.MISSED].includes(tab)) {
    // Add filter to show only the current user's sessions in coaching view
    adjusted_filter.push([{
      table: BackendTables.SESSION_AGENTS,
      column_name: SessionAgentsColumns.CREATOR_AGENT_ID,
      operator: FilterOperator.EQ,
      value_safe: user.user_id
    }]);
  }

  return [...adjusted_filter, ...tab_filter];
}

/**
 * Generate a unique session key for the given parameters
 */
function generateSessionKey(
  filter: BackendFilter,
  tab: SessionHistoryTabs,
  enforced_start: Date | null | undefined,
  enforced_end: Date | null | undefined,
  refreshCount?: number,
  getStartDate?: () => Date | null
): string {
  // Convert dates to ISO strings for consistent serialization
  const startDate = getStartDate ? getStartDate() : (enforced_start ?? null);
  const endDate = enforced_end ?? null;
  
  const keyData = { 
    'cnf': filter,
    'tab': tab,
    'start': startDate ? startDate.toISOString() : null, 
    'end': endDate ? endDate.toISOString() : null,
    'is_forced': !!(enforced_start ?? null),
    'refresh': refreshCount || 0
  };
  return JSON.stringify(keyData);
}

/**
 * Core hook that handles filter generation, session key creation, and returns comprehensive session data.
 * This hook is responsible for:
 * - Converting props to appropriate filters
 * - Generating the session key that would be used for fetching
 * - Managing extended session state with enhancement tracking
 * - Providing read-only access to session items from Redux state
 * - Calculating derived properties like totalItems, monthsSearched, etc.
 * 
 * Can be used directly for read-only access or by session-fetch-hook for full functionality.
 */
export function useSessionItems(props: SessionListProps) {
  const sessionState = useSelector((state: RootState) => state.sessionList);
  const user = useSelector((state: RootState) => state.user.value);
  const refreshCount = useSelector((state: RootState) => state.refresh.forceRefresh);

  // Session enhancement tracking ref
  const sessionEnhancementsRef = useRef<Map<string, {
    totalItems: number;
    backoffRetryCount: number;
    noResultsAfterBackoff: boolean;
    monthsSearched: number;
    hitAbsoluteMaxLookback?: boolean;
    lastUpdated: number;
  }>>(new Map());

  const { passed_filter, enforced_start, enforced_end, tab, view } = props;

  // Core state
  const hasStrictStartDate = enforced_start != null;

  // Calculate the start date to use for key generation and fetching
  const getStartDate = useCallback((
    extendByMonths: number = 0, 
    baseStartDate?: Date | null
  ): Date | null => {
      const maxLookback = ABSOLUTE_MAX_LOOKBACK_MONTHS * MONTH_IN_MS;

    if (tab === SessionHistoryTabs.ALL_HISTORY) return null;
    if (enforced_start) return enforced_start;
    
    if (baseStartDate) {
      // When extending from a base date, go back by the specified months
      const adjustedDate = new Date(baseStartDate.getTime());
      adjustedDate.setMonth(adjustedDate.getMonth() - extendByMonths);
      
      // Respect max lookback limit
      const end = enforced_end ?? new Date();
      if (end.getTime() - adjustedDate.getTime() > maxLookback) {
        adjustedDate.setTime(roundDateDown(new Date(end.getTime() - maxLookback)).getTime());
      }
      return roundDateDown(adjustedDate);
      }
      
      const end = enforced_end ?? new Date();
    const defaultMonthsBack = 1 + extendByMonths;
    
    // Make sure we don't go beyond the max lookback
    if (defaultMonthsBack > ABSOLUTE_MAX_LOOKBACK_MONTHS) {
      return roundDateDown(new Date(end.getTime() - maxLookback));
    }
    
    return roundDateDown(new Date(end.getTime() - (defaultMonthsBack * MONTH_IN_MS)));
  }, [enforced_start, enforced_end, tab]);

  // Generate the combined filter using shared utilities
  const filter = useMemo(() => {
    return generateCombinedFilter(passed_filter, tab, view, user as ReduxSafeUserDataResult | null);
  }, [passed_filter, tab, view, user]);

  // Generate the session key using shared utilities
  const sessionKey = useMemo(() => {
    return generateSessionKey(filter, tab, enforced_start, enforced_end, refreshCount, () => getStartDate());
  }, [filter, tab, enforced_start, enforced_end, refreshCount, getStartDate]);

  // Calculate months searched based on actual date range
  const calculateMonthsSearched = useCallback((startDate?: Date | null): number => {
    if (!startDate) return 1;
    const endDate = enforced_end ?? new Date();
    const monthsDiff = (endDate.getTime() - startDate.getTime()) / MONTH_IN_MS;
    return Math.max(1, Math.ceil(monthsDiff));
  }, [enforced_end]);

  // Get the enhanced session state combining Redux state with our local enhancements
  const getCurrentSession = useCallback((key: string): ExtendedSessionState => {
    if (!key) {
      return {
        items: [],
        startRequested: null,
        hasMore: true,
        startHasMore: true,
        isLoading: false,
        isDebouncing: false,
        error: null,
        lastRequestKey: null,
        totalItems: 0,
        backoffRetryCount: 0,
        noResultsAfterBackoff: false,
        monthsSearched: 1
      };
    }
    
    const reduxState = sessionState.sessions[key];
    if (!reduxState) {
      return {
        items: [],
        startRequested: null,
        hasMore: true,
        startHasMore: true,
        isLoading: false,
        isDebouncing: false,
        error: null,
        lastRequestKey: null,
        totalItems: 0,
        backoffRetryCount: 0,
        noResultsAfterBackoff: false,
        monthsSearched: 1
      };
    }
    
    const enhancement = sessionEnhancementsRef.current.get(key) || { 
      totalItems: reduxState.items.length,
      backoffRetryCount: 0,
      noResultsAfterBackoff: false,
      monthsSearched: 1,
      hitAbsoluteMaxLookback: false,
      lastUpdated: Date.now()
    };
    
    return {
      ...reduxState,
      // Convert Redux-safe items back to original format with Date objects
      items: fromReduxSafeSessionListResponses(reduxState.items),
      // Keep startRequested as ISO string
      startRequested: reduxState.startRequested,
      totalItems: enhancement.totalItems,
      backoffRetryCount: enhancement.backoffRetryCount,
      noResultsAfterBackoff: enhancement.noResultsAfterBackoff,
      monthsSearched: enhancement.monthsSearched,
      hitAbsoluteMaxLookback: enhancement.hitAbsoluteMaxLookback
    };
  }, [sessionState.sessions]);

  // Update our session enhancement tracking
  const updateSessionEnhancement = useCallback((
    key: string,
    enhancement: { 
      totalItems?: number; 
      backoffRetryCount?: number; 
      noResultsAfterBackoff?: boolean;
      monthsSearched?: number;
      hitAbsoluteMaxLookback?: boolean;
    }
  ) => {
    const reduxState = sessionState.sessions[key];
    const current = sessionEnhancementsRef.current.get(key) || { 
      totalItems: reduxState?.items.length || 0,
      backoffRetryCount: 0,
      noResultsAfterBackoff: false,
      monthsSearched: 1,
      hitAbsoluteMaxLookback: false,
      lastUpdated: Date.now()
    };
    
    const updated = {
      ...current,
      ...enhancement,
      lastUpdated: Date.now()
    };
    
    sessionEnhancementsRef.current.set(key, updated);
  }, [sessionState.sessions]);

  // Check if we need more data based on current item count
  const needsMoreData = useCallback((itemCount: number): boolean => {
    return tab !== SessionHistoryTabs.ALL_HISTORY && !hasStrictStartDate && itemCount < MIN_DESIRED_ITEMS;
  }, [hasStrictStartDate, tab]);

  // Memoize the current session state for the key
  const currentSession = useMemo<ExtendedSessionState>(() => 
    getCurrentSession(sessionKey),
    [sessionKey, getCurrentSession]
  );

  // Get the session data from Redux state with enhanced properties
  const sessionData = useMemo(() => {
    return {
      items: currentSession.items || [],
      isLoading: currentSession.isLoading || false,
      hasData: (currentSession.items?.length || 0) > 0,
      count: currentSession.items?.length || 0,
      hasMore: currentSession.hasMore ?? true,
      error: currentSession.error || null,
      totalItems: currentSession.totalItems || (currentSession.items?.length || 0),
      noResultsAfterBackoff: currentSession.noResultsAfterBackoff || false,
      monthsSearched: currentSession.monthsSearched || 1,
      backoffRetryCount: currentSession.backoffRetryCount || 0,
      hitAbsoluteMaxLookback: currentSession.hitAbsoluteMaxLookback || false,
      startRequested: currentSession.startRequested, // Keep as ISO string
      startHasMore: currentSession.startHasMore,
      lastRequestKey: currentSession.lastRequestKey
    };
  }, [currentSession]);

  // Cleanup effect to prevent memory leaks from the sessionEnhancementsRef Map growing indefinitely
  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();
      sessionEnhancementsRef.current.forEach((enhancement, key) => {
        if (enhancement.lastUpdated && now - enhancement.lastUpdated > ENHANCEMENT_CLEANUP_INTERVAL) {
          sessionEnhancementsRef.current.delete(key);
        }
      });
    };

    const intervalId = setInterval(cleanup, ENHANCEMENT_CLEANUP_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  return {
    // Core data
    items: sessionData.items, // Already converted to Date objects in getCurrentSession
    count: sessionData.count,
    hasData: sessionData.hasData,
    isLoading: sessionData.isLoading,
    isDebouncing: currentSession.isDebouncing || false,
    // Consolidated loading state that includes debouncing
    isLoadingOrDebouncing: sessionData.isLoading || currentSession.isDebouncing || false,
    hasMore: sessionData.hasMore,
    error: sessionData.error,
    
    // Enhanced properties
    totalItems: sessionData.totalItems,
    noResultsAfterBackoff: sessionData.noResultsAfterBackoff,
    monthsSearched: sessionData.monthsSearched,
    backoffRetryCount: sessionData.backoffRetryCount,
    hitAbsoluteMaxLookback: sessionData.hitAbsoluteMaxLookback,
    startRequested: sessionData.startRequested, // Keep as ISO string
    startHasMore: sessionData.startHasMore,
    lastRequestKey: sessionData.lastRequestKey,
    
    // Filter and key information for consumers who need it
    filter,
    sessionKey,
    hasStrictStartDate,
    
    // Utility functions
    getStartDate,
    getCurrentSession,
    updateSessionEnhancement,
    calculateMonthsSearched,
    needsMoreData
  };
} 